import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AIGenerateButton } from "@/components/npo/catalog/AIGenerateButton";
import { Textarea } from "@/components/ui/textarea";
import { RotateCcw } from "lucide-react";

interface PolishWithAIDialogProps {
  open: boolean;
  originalText: string;
  fieldLabel?: string;
  context?: string;
  session?: {
    access_token: string;
  } | null;
  onAccept: (polishedText: string) => void;
  onCancel: () => void;
}

export const PolishWithAIDialog: React.FC<PolishWithAIDialogProps> = ({
  open,
  originalText,
  fieldLabel = "Content",
  context = "",
  session,
  onAccept,
  onCancel,
}) => {
  const [loading, setLoading] = useState(false);
  const [polished, setPolished] = useState("");
  const [error, setError] = useState("");

  const handlePolish = async () => {
    setLoading(true);
    setError("");
    try {
      const apiUrl = import.meta.env.VITE_CC_API_URL || "";
      const response = await fetch(`${apiUrl}/v1/tools/polish`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token || ""}`,
        },
        body: JSON.stringify({ context: context || "", text: originalText }),
      });
      if (!response.ok) throw new Error("Failed to polish text");
      const data = await response.json();
      setPolished(data.data || "");
    } catch (err: any) {
      setError(err.message || "Failed to polish text");
    } finally {
      setLoading(false);
    }
  };

  // Reset polished text when dialog opens
  React.useEffect(() => {
    if (open) {
      setPolished("");
      setError("");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Polish {fieldLabel} with AI</DialogTitle>
        </DialogHeader>
        <div className="mb-2">
          <label className="block font-medium mb-1">
            Original {fieldLabel}
          </label>
          <Textarea value={originalText} readOnly className="mb-2" />
        </div>
        <div className="mb-2">
          <label className="block font-medium mb-1">Polished Preview</label>
          <Textarea
            value={polished}
            readOnly
            placeholder="AI-polished text will appear here"
          />
        </div>
        {error && <div className="text-red-600 text-sm mb-2">{error}</div>}
        <DialogFooter className="gap-2">
          {!polished ? (
            <AIGenerateButton
              isGenerating={loading}
              onClick={handlePolish}
            />
          ) : (
            <Button
              variant="outline"
              onClick={() => setPolished("")}
              type="button"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Regenerate
            </Button>
          )}
          <Button
            onClick={() => {
              onAccept(polished);
              onCancel();
            }}
            disabled={!polished}
            type="button"
            variant="default"
          >
            Accept
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
